import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { fetchJson } from '@/api/client'
import { keys } from '@/api/keys'
import { patchSettings, settingsQuery } from '@/api/settings'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

const nameOf = (provider: string) => provider === 'fish' ? 'Fish Audio' : 'Kokoro'
interface VoiceSample { url: string; dur: number; provider: string; model: string; voice: string }

export function TtsSettings() {
  const query = useQuery(settingsQuery)
  const qc = useQueryClient()
  const saved = query.data?.tts
  const [provider, setProvider] = useState<'kokoro' | 'fish'>('kokoro')
  const [voiceId, setVoiceId] = useState('')
  useEffect(() => {
    setProvider(saved?.provider ?? 'kokoro')
    setVoiceId(saved?.fish.voice_id ?? '')
  }, [saved])
  const save = useMutation({
    mutationFn: () => patchSettings({ tts: {
      provider, ...(provider === 'fish' && voiceId.trim() ? { fish: { voice_id: voiceId.trim() } } : {}),
    } }),
    onSuccess: next => {
      qc.setQueryData(keys.settings(), next)
      void qc.invalidateQueries({ queryKey: keys.tts.voices() })
      void qc.invalidateQueries({ queryKey: ['health'] })
    },
  })
  const test = useMutation({
    mutationFn: () => fetchJson<VoiceSample>('/api/tts/test', { method: 'POST',
      body: { provider, ...(provider === 'fish' ? { voice_id: voiceId.trim() } : {}) } }),
  })
  const dirty = provider !== (saved?.provider ?? 'kokoro') || (provider === 'fish' && voiceId.trim() !== (saved?.fish.voice_id ?? ''))
  const busy = save.isPending || test.isPending
  const resetFeedback = () => { save.reset(); test.reset() }

  if (query.isPending) return <p className="text-sm text-ink-2">Loading voice settings…</p>
  if (query.isError) return <p role="alert">Voice settings could not be loaded.</p>
  return (
    <form className="card p-5" aria-labelledby="tts-heading" onSubmit={event => { event.preventDefault(); save.mutate() }}>
      <h2 id="tts-heading" className="text-lg font-semibold text-ink">Story voice</h2>
      <p className="mt-1 text-sm text-ink-2">Choose the voice service for new recordings. Existing stories keep their recorded audio.</p>
      <fieldset className="mt-4 space-y-3" disabled={busy}>
        <legend className="sr-only">Voice provider</legend>
        <div className="flex flex-wrap gap-3">
          {(['kokoro', 'fish'] as const).map(value => (
            <label key={value} className="flex min-h-12 cursor-pointer items-center gap-2 rounded-lg border border-line px-3 focus-within:ring-2 focus-within:ring-accent">
              <input type="radio" name="tts-provider" value={value} checked={provider === value}
                onChange={() => { setProvider(value); resetFeedback() }} />
              {value === 'kokoro' ? 'Kokoro (current default)' : 'Fish Audio'}
            </label>
          ))}
        </div>
        {provider === 'fish' ? <div className="space-y-2">
          <label htmlFor="fish-voice-id" className="block text-sm font-medium text-ink">Fish Audio voice ID</label>
          <Input id="fish-voice-id" name="fish-voice-id" value={voiceId} required pattern="[a-fA-F0-9]{32}"
            maxLength={32} autoComplete="off" spellCheck={false} aria-describedby="fish-voice-help"
            onChange={event => { setVoiceId(event.target.value); resetFeedback() }} />
          <p id="fish-voice-help" className="text-xs text-ink-2">Uses S2.1 Pro Free (s2.1-pro-free). This voice reads all roles; your local role voices below stay saved.</p>
          <p className="text-xs text-ink-2">{saved?.fish.configured ? 'API key configured on the server.' : 'Set FISH_AUDIO_API_KEY on the server before testing or selecting Fish Audio.'}</p>
          <p className="text-xs text-ink-2">Story text is sent to Fish Audio. The free tier is best-effort and requests may be retained for model improvement.{' '}
            <a className="underline" href="https://fish.audio/blog/s2-1-pro-free-api/" target="_blank" rel="noreferrer">Fish Audio details</a>
          </p>
        </div> : <p className="text-sm text-ink-2">Uses the existing local Kokoro service and saved character voices.</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={!dirty} loading={save.isPending}>Save voice provider</Button>
          <Button type="button" onClick={() => test.mutate()} loading={test.isPending}
            disabled={provider === 'fish' && (!saved?.fish.configured || !/^[a-fA-F0-9]{32}$/.test(voiceId.trim()))}>Test voice</Button>
        </div>
      </fieldset>
      <p className="mt-3 text-sm text-ink-2" role="status">Saved voice provider: {nameOf(saved?.provider ?? 'kokoro')}</p>
      {save.error || test.error ? <p className="mt-2 text-sm text-danger" role="alert">{(save.error ?? test.error)?.message}</p> : null}
      {test.data ? <div className="mt-3 space-y-2">
        <p role="status" className="text-sm text-ink-2">Voice is ready — {nameOf(test.data.provider)}, {test.data.dur.toFixed(1)} seconds. Press play to listen.</p>
        <audio aria-label="Voice sample" controls preload="metadata" src={test.data.url} />
      </div> : null}
    </form>
  )
}
